import {
  History as HistoryIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { Box, CircularProgress, IconButton, Paper, Stack, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RecentlyViewedItem } from 'RecentlyViewed';
import useRecentlyViewed from '../../hooks/admin/useRecentlyViewed';
import { generateActionButton } from '../styles/ModelCard';
import { Palette } from 'Palette';
import { RADIUS } from 'DesignTokens';
import { StyledHeading } from '../styles/styles';
import AdminModelCard from './AdminModelCard';
import { RecentlyViewedCompactCards } from './RecentlyViewedCompactCards';
import {
  getModelDisplayName,
  getModelIcon,
  getRecentlyViewedNavigationPath,
  pluralizeModelLabel,
} from './recentlyViewedSidebarUtils';

interface RecentlyViewedSidebarProps {
  modelType: string;
  limit?: number;
  tone?: keyof typeof Palette;
  compact?: boolean;
}

export const RecentlyViewedSidebar: React.FC<RecentlyViewedSidebarProps> = ({
  modelType,
  limit = 5,
  tone = 'tone1',
  compact = false,
}) => {
  const [items, setItems] = useState<RecentlyViewedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<any>(null);

  const { getRecentlyViewed } = useRecentlyViewed();
  const navigate = useNavigate();

  const loadRecentlyViewed = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getRecentlyViewed(modelType, limit, currentPage);
      setItems(response.data);
      setPagination(response.pagination);
    } catch (err) {
      console.warn('Failed to load recently viewed items:', err);
    } finally {
      setLoading(false);
    }
  }, [getRecentlyViewed, modelType, limit, currentPage]);

  useEffect(() => {
    void loadRecentlyViewed();
  }, [loadRecentlyViewed]);

  const handleItemClick = (item: RecentlyViewedItem) => {
    navigate(getRecentlyViewedNavigationPath(item));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={1}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (items.length === 0) {
    return null;
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: compact ? 1.5 : 2,
        bgcolor: Palette[tone].background,
        border: `1px solid ${Palette[tone].border}`,
        borderRadius: RADIUS.section,
      }}
    >
      <Box display="flex" alignItems="center" mb={1}>
        <HistoryIcon
          sx={{
            mr: 1,
            fontSize: 16,
            color: Palette[tone].buttonBg || Palette[tone].border,
          }}
        />
        <StyledHeading headerVariant="subheading" tone={tone}>
          Recently Viewed {pluralizeModelLabel(modelType)}
        </StyledHeading>
      </Box>

      {compact ? (
        <RecentlyViewedCompactCards items={items} tone={tone} onItemClick={handleItemClick} />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {items.map((item, index) => (
            <AdminModelCard
              key={`${item.modelType}-${item.modelId}-${index}`}
              name={getModelDisplayName(item)}
              hideViewButtons
              email=""
              phone=""
              entity={item.modelData || {}}
              tone={tone}
              className="recently-viewed-card"
              icon={React.cloneElement(getModelIcon(item.modelType), {
                sx: { fontSize: 12 },
              })}
              actionButtons={generateActionButton('View Item', getModelIcon(item.modelType), () =>
                handleItemClick(item),
              )}
              onClick={() => handleItemClick(item)}
              allowWrap={true}
              sx={{
                minHeight: '5rem !important',
                height: 'auto',
              }}
            />
          ))}
        </Box>
      )}

      {compact && pagination && pagination.totalPages > 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 1,
            pt: 1,
            borderTop: `1px solid ${Palette[tone].border}`,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Page {pagination.page} of {pagination.totalPages}
          </Typography>

          <Stack direction="row" spacing={0.5}>
            <IconButton
              size="small"
              disabled={!pagination.hasPreviousPage}
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              sx={{ width: 24, height: 24 }}
            >
              <NavigateBeforeIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <IconButton
              size="small"
              disabled={!pagination.hasNextPage}
              onClick={() => setCurrentPage((prev) => prev + 1)}
              sx={{ width: 24, height: 24 }}
            >
              <NavigateNextIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Stack>
        </Box>
      )}
    </Paper>
  );
};

export default RecentlyViewedSidebar;
